import React, { useEffect, useRef } from 'react';

interface RadiusDropdownProps {
  onSelectRadius: (radius: number) => void;
  onClose: () => void;
  isOpen: boolean;
}

const RadiusDropdown: React.FC<RadiusDropdownProps> = ({ onSelectRadius, onClose, isOpen }) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Use a flag to skip the first click (which is the button click that opened the dropdown)
    let skipNext = true;
    const handleClickWithSkip = (event: MouseEvent) => {
      if (skipNext) {
        skipNext = false;
        return;
      }
      handleClickOutside(event);
    };

    // Add listener after a small delay to skip the opening click
    const timerId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickWithSkip);
    }, 50);

    return () => {
      clearTimeout(timerId);
      document.removeEventListener('mousedown', handleClickWithSkip);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const radiusOptions = [
    { value: 10, label: '10 km' },
    { value: 20, label: '20 km' },
    { value: 30, label: '30 km' },
    { value: 50, label: '50 km' },
  ];

  return (
    <div
      ref={dropdownRef}
      className="absolute top-full mt-1 right-0 bg-white border-2 border-gray-200 rounded-lg shadow-lg z-50 min-w-[120px] overflow-hidden"
    >
      {radiusOptions.map((option) => (
        <button
          key={option.value}
          onClick={() => {
            onSelectRadius(option.value);
            onClose();
          }}
          className="w-full px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors first:rounded-t-md last:rounded-b-md"
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

export default RadiusDropdown;

